import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { arDigits, fmtLocaleNum, fmtIsoWeekLabel, fmtIsoDate } from "@/lib/utils";
import { weekValueBn, displayBiggestSale } from "@/lib/weekValue";

const Stat = ({ label, value }) => (
  <div><p className="text-[10px] uppercase tracking-[0.16em] text-propertic-muted mb-1">{label}</p><p className="font-semibold tabular-nums">{value}</p></div>
);

export const LatestWeekCard = ({ w, isAR, lh, format, formatBn }) => (
  <div className="mt-10 sm:mt-14 rounded-3xl border border-propertic-gold/40 bg-white/80 p-6 sm:p-8" data-testid="insights-latest-week">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-1">{isAR ? "آخر أسبوع" : "Latest week"}</p>
        <p className="font-editorial text-2xl sm:text-3xl">{fmtIsoWeekLabel(w.iso_week, isAR)}</p>
        <p className="text-[12px] text-propertic-muted">{fmtIsoDate(w.start_date, isAR)} → {fmtIsoDate(w.end_date, isAR)}</p>
      </div>
      <Link
        to={lh(`/insights/weekly/${w.iso_week}`)}
        data-testid="insights-latest-week-cta"
        className="inline-flex items-center gap-2 rounded-full h-10 px-5 border border-propertic-navy/30 hover:border-propertic-gold hover:bg-propertic-sand text-xs font-mono-brand tracking-[0.14em] uppercase transition-colors"
      >
        {isAR ? "التفاصيل" : "Full recap"}
        <ArrowUpRight className="w-3.5 h-3.5" />
      </Link>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
      <Stat label={isAR ? "الصفقات" : "Transactions"} value={fmtLocaleNum(w.tx_count, isAR)} />
      <Stat label={isAR ? "القيمة" : "Value"} value={formatBn(weekValueBn(w))} />
      <Stat label={isAR ? "أعلى صفقة" : "Biggest sale"} value={displayBiggestSale(w) ? format(displayBiggestSale(w).value_aed) : "—"} />
      <Stat label={isAR ? "حصة قيد الإنشاء" : "Off-plan"} value={isAR ? `${arDigits(w.offplan_share_pct)}٪` : `${w.offplan_share_pct}%`} />
    </div>
  </div>
);

const Def = ({ testid, dot, tone, title, body }) => (
  <div className="rounded-2xl bg-white/80 border hairline p-4 sm:p-5" data-testid={testid}>
    <p className={`font-mono-brand ${tone} text-[10px] tracking-[0.22em] uppercase mb-2 inline-flex items-center gap-2`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden="true" />
      {title}
    </p>
    <p className="text-[13px] text-propertic-ink-2/95 leading-relaxed">{body}</p>
  </div>
);

const pill = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border hairline hover:bg-propertic-navy hover:text-propertic-paper transition-colors";

// Methodology + definitions: original Insights wording, moved below the weekly reports.
export const Methodology = ({ isAR, lh }) => (
  <section className="mt-14 sm:mt-16 rounded-3xl border hairline bg-propertic-sand/40 p-6 sm:p-8" data-testid="insights-how-to-read">
    <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-2">{isAR ? "المنهجية" : "How to read"}</p>
    <h2 className="font-editorial text-2xl sm:text-3xl leading-[1.15] text-propertic-navy mb-4">
      {isAR ? "كيف تقرأ بيانات بروبيرتك لسوق دبي" : "How to read Propertic's Dubai market data"}
    </h2>
    <p className="text-[14px] sm:text-[15px] text-propertic-ink-2/95 leading-relaxed mb-4" data-testid="insights-source-method">
      {isAR
        ? "السجلات الأساسية للصفقات مأخوذة مباشرة من بوابة البيانات المفتوحة لدائرة الأراضي والأملاك في دبي، وتُحدَّث كل يوم اثنين الساعة ١١:٠٠ بتوقيت الإمارات المحلي. أما المؤشرات المشتقة (حصة قيد الإنشاء، التغيّر السنوي، الوسيط) فتُحتسب من قِبل بروبيرتك وفق المنهجية الموضحة بجانب كل مؤشر."
        : "Underlying transaction records are sourced from the Dubai Land Department Open Data Gateway, refreshed every Monday at 11:00 UAE local time (Monday-morning timing ensures Sunday's DLD registrations have been published). Derived metrics (off-plan share, YoY change, medians) are calculated by The Propertic using the methodology shown beside each metric."}
    </p>
    <p className="text-[14px] sm:text-[15px] text-propertic-ink-2/95 leading-relaxed mb-6">
      {isAR
        ? "تعتمد تقارير بروبيرتك السوقية على بيانات صفقات دائرة الأراضي والأملاك في دبي لتتبّع نشاط سوق العقارات في دبي. تشير أرقام المبيعات إلى صفقات البيع الصافية ما لم يُذكر خلاف ذلك. أمّا إجراءات الرهن والهبة فتُعرض بشكل منفصل عند تغطية إجمالي الإجراءات."
        : "Propertic's market reports use Dubai Land Department transaction data to track Dubai real-estate activity. Sales figures refer to pure sales transactions unless otherwise stated. Mortgage and gift procedures are reported separately when the analysis covers total procedures."}
    </p>
    <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
      <Def testid="insights-pure-sales-def" dot="bg-emerald-500" tone="text-emerald-700" title={isAR ? "المبيعات الصافية" : "Pure sales"}
        body={isAR
          ? "المبيعات الصافية هي صفقات بيع العقار المكتملة كما أعلنتها دائرة الأراضي والأملاك. تُفصَل عن إجراءات الرهن والهبة حتى لا يتضخّم حجم المبيعات ولا قيمتها بأنواع أخرى من الإجراءات."
          : "Pure sales are completed property sale transactions reported by the Dubai Land Department. They are separated from mortgage and gift procedures so that sales volume and sales value are not inflated by other transaction types."} />
      <Def testid="insights-total-procedures-def" dot="bg-propertic-gold" tone="text-propertic-gold" title={isAR ? "إجمالي الإجراءات" : "Total procedures"}
        body={isAR
          ? "يجمع إجمالي الإجراءات بين المبيعات والرهون والهبات. يقيس مجمل الإجراءات المسجَّلة لدى الدائرة، ولا يجب تفسيره على أنّه مبيعات عقارية صافية."
          : "Total procedures combine sales, mortgages and gifts. They measure overall recorded DLD procedures and should not be interpreted as pure property sales."} />
    </div>
    <div className="mt-6 rounded-2xl border border-propertic-gold/40 bg-white/60 p-4 sm:p-5" data-testid="insights-dataset-legend">
      <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.2em] uppercase mb-2">
        {isAR ? "قواميس المصطلحات · مبيعات مقابل إجراءات" : "Data dictionary · Sales vs Procedures"}
      </p>
      <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 text-[12.5px] leading-relaxed text-propertic-ink-2/95">
        <p>
          <span className="font-semibold text-propertic-navy">{isAR ? "«الصفقات» = " : "\"Sales\" = "}</span>
          {isAR
            ? "صفقات بيع عقارية فعلية فقط (سكني + تجاري). هذا الرقم يظهر في الصفحة الرئيسية."
            : "actual property purchase transactions only (residential + commercial). This is the number shown on the homepage."}
        </p>
        <p>
          <span className="font-semibold text-propertic-navy">{isAR ? "«الإجراءات» = " : "\"Procedures\" = "}</span>
          {isAR
            ? "جميع الإجراءات المسجّلة لدى دائرة الأراضي: بيع + رهون عقارية + هبات + عمليات نقل. هذا الرقم يظهر في التقارير الأسبوعية أعلاه."
            : "every procedure registered with DLD: sales + mortgage registrations + gift transfers + other transfers. This is the number shown in the weekly reports above."}
        </p>
      </div>
    </div>
    <div className="mt-6 pt-6 border-t hairline flex flex-wrap gap-2 sm:gap-3 text-[11px] font-mono-brand tracking-[0.14em] uppercase" data-testid="insights-internal-links">
      {[
        ["/dubai-market", isAR ? "بيانات السوق" : "Market data", "insights-link-market"],
        ["/dubai-compare-areas", isAR ? "قارن بين منطقتين" : "Compare areas", "insights-link-compare"],
        ["/uae-mortgage", isAR ? "قواعد الرهن العقاري" : "Mortgage rules", "insights-link-mortgage"],
        ["/aiagent", isAR ? "اسأل روني" : "Ask Roni", "insights-link-roni"],
      ].map(([to, label, id]) => (
        <Link key={id} to={lh(to)} className={pill} data-testid={id}>
          {label}
          <ArrowUpRight className="w-3 h-3" />
        </Link>
      ))}
    </div>
  </section>
);
